import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { Button } from './dyn/components/Button/Button';
import { IconDynLogo } from './dyn/icons/index';

const POLL_INTERVAL_MS = 1000;

const TABS = ['Text', 'HTML', 'Axis', 'JSON'] as const;
type Tab = typeof TABS[number];

const RainbowBorder = ({ active, children }: { active: boolean; children: ReactNode }) => (
  <div style={{ position: 'relative', borderRadius: '8px' }}>
    <div style={{
      position: 'absolute', inset: 0,
      borderRadius: '8px',
      background: active
        ? 'linear-gradient(135deg, #22d2e6, #2b65ff, #8000ff, #ff0080, #ff6262, #62ff98)'
        : 'rgba(255,255,255,0.16)',
      animation: active ? 'aiHue 4s linear infinite' : 'none',
      boxShadow: active ? '0 0 10px 2px #22d2e655, 0 0 24px 4px #2b65ff33' : 'none',
    }} />
    <div style={{ position: 'relative', margin: active ? '3px' : '1px', borderRadius: '6px', background: 'var(--dyn-color-blue-bg1)', overflow: 'hidden', isolation: 'isolate' }}>
      {children}
    </div>
    <style>{`@keyframes aiHue{0%{filter:hue-rotate(0deg)}100%{filter:hue-rotate(360deg)}}`}</style>
  </div>
);

function App() {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [axisText, setAxisText] = useState('');
  const [jsonText, setJSONText] = useState('');
  const [isLinkOnceToggled, setLinkOnceToggler] = useState(true);
  const [isEventsOnlyToggled, setEventsOnlyToggler] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCopyCheckIcon, setShowCopyCheckIcon] = useState(false);
  const [viewMode, setViewMode] = useState<Tab>('Text');
  const [showHelp, setShowHelp] = useState(() => !localStorage.getItem('ner_help_seen'));
  const [todayCount, setTodayCount] = useState<number | null>(null);

  const pollTimerRef = useRef<number | null>(null);
  const API_BASE = (process.env.REACT_APP_NER_API_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetch(`${API_BASE}/stats`, { headers: { 'X-Api-Key': process.env.REACT_APP_NER_API_KEY! } })
      .then(r => r.json())
      .then(d => setTodayCount(d.count ?? 0))
      .catch(() => {});
  }, [API_BASE]);

  const pollJob = async (jid: string) => {
    try {
      const res = await fetch(`${API_BASE}/jobs/${jid}`, {
        headers: { 'X-Api-Key': process.env.REACT_APP_NER_API_KEY! }
      });
      if (!res.ok) {
        console.error('Polling error:', res.status, res.statusText);
        if (res.status === 404) {
          setOutputText('Error while detecting Entities');
          setAxisText('Error while detecting Entities');
          setJSONText('Error while detecting Entities');
          setIsProcessing(false);
          return;
        }
        pollTimerRef.current = window.setTimeout(() => pollJob(jid), POLL_INTERVAL_MS);
        return;
      }
      const data = await res.json();
      console.log('NER Job Status Response:', JSON.stringify(data, null, 2));
      if (data.status === 'SUCCEEDED') {
        const result = data.result || {};
        const hasLinkedText = result.html_linked_text && result.html_linked_text !== inputText;
        if (Array.isArray(result.detected_entities) && result.detected_entities.length === 0 && !hasLinkedText) {
          setOutputText('📭 No entities found in D3 Database');
          setAxisText('📭 No entities found in D3 Database');
          setJSONText('📭 No entities found in D3 Database');
        } else if (result.message && !hasLinkedText) {
          setOutputText(`📭 ${result.message}`);
          setAxisText(`📭 ${result.message}`);
          setJSONText(JSON.stringify(result, null, 2));
        } else {
          setOutputText(result.html_linked_text || '');
          setAxisText(result.axis_linked_text || '');
          setJSONText(JSON.stringify(result, null, 2));
          handleCopy();
        }
        setIsProcessing(false);
        return;
      }
      if (data.status === 'FAILED') {
        console.error('NER job failed:', data.error);
        setOutputText(`Error while detecting Entities: "${data.error || ''}"`);
        setAxisText('Error while detecting Entities');
        setJSONText('Error while detecting Entities');
        setIsProcessing(false);
        return;
      }
      pollTimerRef.current = window.setTimeout(() => pollJob(jid), POLL_INTERVAL_MS);
    } catch (err) {
      console.error('Polling exception:', err);
      pollTimerRef.current = window.setTimeout(() => pollJob(jid), POLL_INTERVAL_MS);
    }
  };

  useEffect(() => () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); }, []);

  const handleLinkBtn = async () => {
    console.log(new Date().toLocaleTimeString('de-DE', { hour12: false }), 'Link button clicked');
    setOutputText('🔗 Linking...');
    setAxisText('🔗 Linking...');
    setJSONText('🔗 Linking...');
    setIsProcessing(true);
    if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
    try {
      const res = await fetch(`${API_BASE}/jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Api-Key': process.env.REACT_APP_NER_API_KEY! },
        body: JSON.stringify({ text: inputText, linkAll: !isLinkOnceToggled, eventsOnly: isEventsOnlyToggled })
      });
      if (!res.ok) {
        if (res.status === 401) throw new Error('Error - Unauthorized - Check your API Key');
        else if (res.status === 403) throw new Error('Error - API Access denied');
        else if (res.status === 429) throw new Error('RATE_LIMIT');
        else if (res.status >= 500) throw new Error('Error - Server error - Try again or contact the admin');
        else throw new Error(`Error - HTTP ${res.status}: ${res.statusText}`);
      }
      const submitData = await res.json();
      console.log('NER Submit Response:', JSON.stringify(submitData, null, 2));
      if (!submitData.jobId) throw new Error('Error - No jobId returned from server');
      console.log('NER job queued:', submitData.jobId);
      pollJob(submitData.jobId);
      fetch(`${API_BASE}/stats`, { headers: { 'X-Api-Key': process.env.REACT_APP_NER_API_KEY! } })
        .then(r => r.json())
        .then(d => setTodayCount(d.count ?? 0))
        .catch(() => {});
    } catch (error: any) {
      console.error(new Date().toLocaleTimeString('de-DE', { hour12: false }), 'Error submitting NER job:', error);
      const msg = error?.message === 'RATE_LIMIT'
        ? '🔥 Die KI ist überlastet! Bitte versuche es später oder morgen erneut. Work Life Balance gilt auch für Silizium, Jung!'
        : 'Error while detecting Entities';
      setOutputText(msg);
      setAxisText(msg);
      setJSONText(msg);
      setIsProcessing(false);
    }
  };

  const handleCopy = async () => {
    if (!outputText) return;
    try {
      switch (viewMode) {
        case 'Text':
          try {
            const blob = new Blob([outputText], { type: 'text/html' });
            await navigator.clipboard.write([new ClipboardItem({ 'text/html': blob })]);
          } catch { await navigator.clipboard.writeText(outputText.replace(/<[^>]*>/g, '')); }
          break;
        case 'Axis': await navigator.clipboard.writeText(axisText); break;
        case 'HTML': await navigator.clipboard.writeText(outputText); break;
        case 'JSON': await navigator.clipboard.writeText(jsonText); break;
      }
      setShowCopyCheckIcon(true);
      setTimeout(() => setShowCopyCheckIcon(false), 1000);
    } catch (err) { console.error('Copy failed:', err); }
  };

  const currentOutput = viewMode === 'Axis' ? axisText : viewMode === 'JSON' ? jsonText : outputText;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--dyn-color-blue-bg1)', padding: '0 0 3rem' }}>
      {/* Header */}
      <header style={{
        background: 'var(--dyn-color-blue-midnight)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        padding: '1rem 2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <img src="/Dyn_Logo_White_rgb_transparent.png" alt="Dyn" style={{ height: 33, display: 'block' }} />
          <span style={{
            fontFamily: 'var(--dyn-font-sans)',
            fontSize: 'var(--dyn-font-size-3xl)',
            textTransform: 'uppercase',
            fontWeight: 700,
            letterSpacing: '0.02em',
            color: 'var(--dyn-color-white)',
          }}>
            Linker
          </span>
        </div>
      </header>

      <main style={{ margin: '0 auto', padding: '2rem 2.5vw', width: '95vw' }}>
        {/* Info Panel */}
        <div style={{
          display: 'grid',
          gridTemplateRows: showHelp ? '1fr' : '0fr',
          transition: 'grid-template-rows 0.35s ease',
          marginBottom: showHelp ? '2rem' : '0',
          transitionProperty: 'grid-template-rows, margin-bottom',
        }}>
          <div style={{ overflow: 'hidden' }}>
            <div style={{
              background: 'var(--dyn-color-blue-surface-dark)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '0.5rem',
              padding: '1.25rem 1.5rem',
              color: 'var(--dyn-color-gray-73)',
              fontSize: 'var(--dyn-font-size-base)',
              lineHeight: 1.7,
              opacity: showHelp ? 1 : 0,
              transform: showHelp ? 'translateY(0)' : 'translateY(-8px)',
              transition: 'opacity 0.3s ease 0.05s, transform 0.3s ease 0.05s',
            }}>
              <p style={{ margin: '0 0 0.75rem' }}>
                Mit diesem Tool können Teams, Wettbewerbe und konkrete Spielereignisse in einem Sporttext verlinkt werden –
                sowohl zukünftige als auch vergangene Events.
                Ein KI-Modell erkennt Teams und Wettbewerbe automatisch, prüft deren Existenz in der Datenbank
                und verlinkt sie mit der Dyn-Plattform.
              </p>
              <p style={{ margin: '0 0 0.75rem' }}>
                Wurden Teams oder Wettbewerbe gefunden, wird der verlinkte Text rechts angezeigt und automatisch in die
                Zwischenablage kopiert ✨
              </p>
              <p style={{ margin: '0 0 0.75rem' }}>
                <strong style={{ color: 'var(--dyn-color-gray-90)' }}>Dyn Sport-Marken:</strong>{' '}
                Begriffe wie <em>Dyn Handball</em>, <em>Dyn Basketball</em>, <em>Dyn Volleyball</em>, <em>Dyn Tischtennis</em> und <em>Dyn Hockey</em> werden
                automatisch auf die jeweilige Sportart-Landingpage von dyn.sport verlinkt – unabhängig von der Datenbank.
              </p>
              <p style={{ margin: '0 0 0.75rem' }}>
                <strong style={{ color: 'var(--dyn-color-gray-90)' }}>Jeden Namen nur einmal verlinken:</strong>{' '}
                Ist diese Option aktiv, wird jedes Team und jeder Wettbewerb nur beim ersten Vorkommen im Text verlinkt –
                alle weiteren Erwähnungen bleiben als einfacher Text erhalten.
                Wird z.B. ein Team am Anfang des Textes genannt und taucht später nochmal auf,
                erhält nur die erste Erwähnung einen Link.
              </p>
              <p style={{ margin: '0 0 0.75rem' }}>
                <strong style={{ color: 'var(--dyn-color-gray-90)' }}>Nur Spiele verlinken:</strong>{' '}
                Ist diese Option aktiv, werden Teams und Wettbewerbe nicht verlinkt. Stattdessen versucht das KI-Modell ausschließlich ein konkretes Spiel im Text zu erkennen –
                also ein Duell zwischen genau zwei Teams. Wird ein passendes Event in der Datenbank gefunden,
                sucht das Modell einen geeigneten Anker im Text, bevorzugt Zeitangaben wie „heute“, „am Samstag“
                oder „am 5. Spieltag“. Nur wenn keine solche Formulierung vorhanden ist, wird ein Teamname als
                Anker verwendet. Verlinkt werden sowohl zukünftige als auch vergangene Spiele.
                Bei laufenden Spielen wird auf den Livestream verlinkt, bei abgeschlossenen auf die Highlights bzw. Wiederholung.
              </p>
              <p style={{ margin: 0 }}>
                <strong style={{ color: 'var(--dyn-color-gray-90)' }}>Ausgabeformate:</strong>{' '}
                Über die Tabs rechts kann zwischen verschiedenen Formaten gewechselt werden –
                <em> Text</em> (gerenderte Vorschau), <em>HTML</em> (roher HTML-Code),
                <em> Axis</em> (Axis-CMS-Format) und <em>JSON</em> (vollständige API-Antwort).
                Mit dem Kopieren-Button wird das aktive Format in die Zwischenablage übernommen.
              </p>
            </div>
          </div>
        </div>

        {/* Main grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0,1fr) 40px minmax(0,1fr)',
          gap: '1.5rem',
          alignItems: 'stretch',
          minHeight: '600px',
        }}>
          {/* Input */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <label style={{
                fontFamily: 'var(--dyn-font-bebas)',
                letterSpacing: '0.05em',
                color: 'var(--dyn-color-gray-90)',
                fontSize: 'var(--dyn-font-size-2xl)',
              }}>
                INPUT TEXT
              </label>
              <button
                onClick={() => {
                  const next = !showHelp;
                  setShowHelp(next);
                  if (next) localStorage.removeItem('ner_help_seen');
                  else localStorage.setItem('ner_help_seen', '1');
                }}
                aria-label="Hilfe ein-/ausblenden"
                style={{
                  width: '1.75rem', height: '1.75rem',
                  borderRadius: '50%',
                  border: `1px solid ${showHelp ? 'var(--dyn-color-blue-active)' : 'rgba(255,255,255,0.25)'}`,
                  background: showHelp ? 'var(--dyn-color-blue-active)' : 'transparent',
                  color: showHelp ? 'var(--dyn-color-blue-midnight)' : 'var(--dyn-color-blue-active)',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 'bold',
                  lineHeight: 1,
                  padding: 0,
                  transition: 'all 0.15s ease',
                }}
              >
                ?
              </button>
            </div>
            <textarea
              placeholder="Sporttext hier einfügen..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              autoFocus
              rows={16}
              style={{
                width: '100%',
                flex: 1,
                minHeight: '500px',
                padding: '1rem 1.25rem',
                borderRadius: '0.5rem',
                border: '1px solid transparent',
                background: 'var(--dyn-color-blue-surface-dark)',
                color: 'var(--dyn-color-white)',
                fontFamily: 'var(--dyn-font-sans)',
                fontSize: 'var(--dyn-font-size-lg)',
                resize: 'vertical',
                boxSizing: 'border-box',
                outline: 'none',
                lineHeight: 1.5,
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--dyn-color-blue-active)'}
              onBlur={(e) => e.target.style.borderColor = 'transparent'}
            />

            {/* Controls below input */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', cursor: 'pointer', userSelect: 'none' }}>
                <span
                  onClick={() => setLinkOnceToggler(!isLinkOnceToggled)}
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '2.25rem',
                    height: '1.25rem',
                    borderRadius: '999px',
                    background: isLinkOnceToggled ? 'var(--dyn-color-blue-medium)' : 'rgba(255,255,255,0.2)',
                    transition: 'background 0.25s ease',
                    flexShrink: 0,
                  }}
                >
                  <span style={{
                    position: 'absolute',
                    top: '0.125rem',
                    left: isLinkOnceToggled ? 'calc(100% - 1.125rem)' : '0.125rem',
                    width: '1rem',
                    height: '1rem',
                    borderRadius: '50%',
                    background: 'var(--dyn-color-white)',
                    transition: 'left 0.25s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  }} />
                </span>
                <span style={{ color: 'var(--dyn-color-gray-73)', fontSize: 'var(--dyn-font-size-base)' }}>
                  Jeden Namen nur einmal verlinken
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', cursor: 'pointer', userSelect: 'none' }}>
                <span
                  onClick={() => setEventsOnlyToggler(!isEventsOnlyToggled)}
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '2.25rem',
                    height: '1.25rem',
                    borderRadius: '999px',
                    background: isEventsOnlyToggled ? 'var(--dyn-color-blue-medium)' : 'rgba(255,255,255,0.2)',
                    transition: 'background 0.25s ease',
                    flexShrink: 0,
                  }}
                >
                  <span style={{
                    position: 'absolute',
                    top: '0.125rem',
                    left: isEventsOnlyToggled ? 'calc(100% - 1.125rem)' : '0.125rem',
                    width: '1rem',
                    height: '1rem',
                    borderRadius: '50%',
                    background: 'var(--dyn-color-white)',
                    transition: 'left 0.25s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  }} />
                </span>
                <span style={{ color: 'var(--dyn-color-gray-73)', fontSize: 'var(--dyn-font-size-base)' }}>
                  Nur Spiele verlinken
                </span>
              </label>
            </div>

            <Button
              variant="primary"
              fullWidth
              textSize="large"
              onClick={handleLinkBtn}
              disabled={isProcessing || !inputText.trim()}
              loading={isProcessing}
              style={{ marginTop: '1rem' }}
            >
              {isProcessing ? 'Wird verlinkt...' : '🔗 Link it!'}
            </Button>
          </div>

          {/* Arrow */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: 'var(--dyn-color-blue-active)', fontSize: '1.5rem' }}>→</span>
          </div>

          {/* Output */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Tab bar */}
            <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem' }}>
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setViewMode(tab)}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '0.375rem',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'var(--dyn-font-bebas)',
                    letterSpacing: '0.05em',
                    fontSize: 'var(--dyn-font-size-lg)',
                    background: viewMode === tab ? 'var(--dyn-color-blue-surface-dark)' : 'transparent',
                    color: viewMode === tab ? 'var(--dyn-color-blue-active)' : 'var(--dyn-color-gray-73)',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>

            <RainbowBorder active={isProcessing}>
              {viewMode === 'Text' ? (
                <div
                  style={{
                    padding: '0.875rem 1rem',
                    minHeight: '360px',
                    color: 'var(--dyn-color-white)',
                    fontFamily: 'var(--dyn-font-sans)',
                    fontSize: 'var(--dyn-font-size-lg)',
                    lineHeight: 1.6,
                    overflowY: 'auto',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: outputText || '<i style="color:var(--dyn-color-gray-73)">Verlinkter Text erscheint hier...</i>'
                  }}
                />
              ) : (
                <textarea
                  readOnly
                  value={currentOutput}
                  placeholder={
                    viewMode === 'HTML' ? 'HTML mit verlinkten Entitäten erscheint hier...' :
                    viewMode === 'Axis' ? 'Axis-Format erscheint hier...' :
                    'JSON-Antwort erscheint hier...'
                  }
                  rows={12}
                  style={{
                    width: '100%',
                    minHeight: '360px',
                    padding: '1rem 1.25rem',
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--dyn-color-white)',
                    fontFamily: viewMode === 'JSON' ? 'monospace' : 'var(--dyn-font-sans)',
                    fontSize: 'var(--dyn-font-size-base)',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    outline: 'none',
                    lineHeight: 1.5,
                  }}
                />
              )}
            </RainbowBorder>

            {/* Copy row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1.25rem' }}>
              <Button variant="secondary" textSize="large" onClick={handleCopy} disabled={!outputText}>
                Kopieren ⎘
              </Button>
              <span style={{
                color: 'var(--dyn-color-green-success)',
                fontSize: 'var(--dyn-font-size-base)',
                opacity: showCopyCheckIcon ? 1 : 0,
                transition: 'opacity 0.4s ease',
              }}>
                ✓ Kopiert!
              </span>
            </div>
          </div>
        </div>
      </main>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 767px) {
          main > div[style*="grid-template-columns"] {
            grid-template-columns: 1fr !important;
          }
          main > div[style*="grid-template-columns"] > div:nth-child(2) {
            transform: rotate(90deg);
            padding-top: 0 !important;
          }
        }
        textarea::placeholder { color: var(--dyn-color-gray-73); }
        .dyn-bt a { color: inherit; }
      `}</style>

      {/* Usage counter */}
      {todayCount !== null && (
        <div style={{
          position: 'fixed',
          bottom: '1rem',
          left: '1.25rem',
          color: 'var(--dyn-color-gray-73)',
          fontSize: 'var(--dyn-font-size-sm)',
          fontFamily: 'var(--dyn-font-sans)',
          opacity: 0.6,
          pointerEvents: 'none',
        }}>
          Heute verwendet: {todayCount}x
        </div>
      )}
    </div>
  );
}

export default App;
